import { useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { Instagram, Mail, ArrowUpRight, ArrowRight, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/constants/testIds";
import { API, BRAND } from "@/lib/config";

const faqs = [
  { q: "Is VISION a clothing brand?", a: "VISION is a lifestyle movement first. Apparel, journals, and guides are simply objects that help carry the practice — the intent is what matters." },
  { q: "How does shipping work?", a: "Every physical order ships through our Payhip storefront. Rates, timing, and returns are handled there, and confirmation is emailed instantly." },
  { q: "Where are your products made?", a: "We work with a small, considered supply chain — premium fabrics, honest manufacturing, and small batch releases so nothing feels disposable." },
  { q: "How can I get involved in the community?", a: "Subscribe to the journal above — early gatherings, city meets, and creator invites go out there first. You can also reach us directly on Instagram." },
  { q: "Do you accept collaborations?", a: "Occasionally, and only when aligned. Reach out with your story — we read every note." },
];

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading" || status === "success") return; // duplicate suppression
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setError("Please share your name, email, and a short message.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
      };
      await axios.post(`${API}/contact/message`, payload);
      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isDup = err?.response?.status === 409;
      setError(isDup ? "Looks like that note already came through. We have it." : (typeof detail === "string" ? detail : "Something went wrong. Please try again in a moment."));
      setStatus("error");
    }
  };

  const disabled = status === "loading" || status === "success";
  const fieldCls =
    "w-full bg-transparent border-0 border-b border-[#2a343a] focus:border-[#e8ecef] outline-none py-3 text-[#e8ecef] placeholder:text-[#4a565c] text-base transition-colors duration-500 disabled:opacity-60";
  const labelCls = "block text-[10px] tracking-[0.32em] uppercase text-[#8a9498] mb-2";

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative text-[#e8ecef] py-32 md:py-48"
      style={{ backgroundImage: "linear-gradient(to bottom, #0f1216 0%, #0a0a0a 40%, #050505 100%)" }}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16 md:mb-20">
          <Reveal>
            <p className="text-[11px] tracking-[0.38em] uppercase text-[#8a9498] mb-8">
              (08) — Reach Out
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Let&rsquo;s<br />
              <span className="font-light text-[#8a9498]">stay in touch.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-[#8a9498] text-base md:text-lg max-w-lg leading-relaxed font-light">
              A short note goes a long way. Tell us who you are — collaborations,
              press, wholesale, or a hello. We read every one.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 md:gap-20">
          {/* Left — Contact form */}
          <div className="lg:col-span-7">
            <Reveal>
              {status === "success" ? (
                <div
                  data-testid="contact-success"
                  className="border border-[#2a343a] bg-[#0f1216] p-8 md:p-12"
                >
                  <div className="w-11 h-11 rounded-full bg-[#e8ecef] flex items-center justify-center mb-6">
                    <Check size={18} className="text-[#0a0a0a]" strokeWidth={2} />
                  </div>
                  <p className="text-[10px] tracking-[0.36em] uppercase text-[#8a9498] mb-4">
                    Message received
                  </p>
                  <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight font-light text-[#e8ecef] max-w-xl">
                    Thank you! Your message has been received. I appreciate you
                    reaching out and will personally get back to you as soon as
                    possible.
                  </p>
                  <button
                    onClick={() => { setStatus("idle"); setError(""); }}
                    data-testid="contact-send-another"
                    className="mt-10 text-[11px] tracking-[0.3em] uppercase border-b border-[#8a9498] pb-1 hover:text-[#e8ecef] hover:border-[#e8ecef] transition-colors duration-500"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="space-y-8"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className={labelCls} htmlFor="cf-name">Full Name *</label>
                      <input id="cf-name" name="name" data-testid="contact-name-input" type="text" required maxLength={120} disabled={disabled} value={form.name} onChange={onChange} className={fieldCls} placeholder="Your name" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-email">Email *</label>
                      <input id="cf-email" name="email" data-testid="contact-email-input" type="email" required disabled={disabled} value={form.email} onChange={onChange} className={fieldCls} placeholder="you@example.com" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-phone">Phone</label>
                      <input id="cf-phone" name="phone" data-testid="contact-phone-input" type="tel" disabled={disabled} value={form.phone} onChange={onChange} className={fieldCls} placeholder="Optional" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-subject">Subject</label>
                      <input id="cf-subject" name="subject" data-testid="contact-subject-input" type="text" maxLength={200} disabled={disabled} value={form.subject} onChange={onChange} className={fieldCls} placeholder="Optional" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-message">Message *</label>
                    <textarea id="cf-message" name="message" data-testid="contact-message-input" required rows={5} maxLength={4000} disabled={disabled} value={form.message} onChange={onChange} className={fieldCls + " resize-none"} placeholder="What&rsquo;s on your mind?" />
                  </div>

                  {status === "error" && (
                    <p data-testid="contact-error" className="text-sm text-red-300/85 font-light">{error}</p>
                  )}

                  <div className="pt-2 flex items-center gap-6">
                    <button
                      type="submit"
                      disabled={disabled}
                      data-testid="contact-submit"
                      aria-busy={status === "loading"}
                      className="group inline-flex items-center gap-3 bg-[#e8ecef] text-[#0a0a0a] px-10 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#c9d3d8] transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "loading" ? (
                        <>
                          <span
                            aria-hidden
                            className="inline-block w-3 h-3 border-2 border-[#0a0a0a] border-t-transparent rounded-full animate-spin"
                          />
                          Sending
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#8a9498]/70">
                      Replies come directly from the Vision team.
                    </p>
                  </div>
                </form>
              )}
            </Reveal>
          </div>

          {/* Right — quick links + FAQ */}
          <div className="lg:col-span-5 space-y-14">
            <div className="space-y-6">
              <Reveal delay={0.1}>
                <a
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={CONTACT.instagram}
                  className="group flex items-center justify-between border-b border-[#1e242a] pb-5 hover:border-[#e8ecef] transition-colors duration-500"
                >
                  <div className="flex items-center gap-4">
                    <Instagram size={20} strokeWidth={1.4} />
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a9498]">Instagram</p>
                      <p className="font-serif text-xl md:text-2xl mt-1 text-[#e8ecef]">{BRAND.instagramHandle}</p>
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
              <Reveal delay={0.15}>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid={CONTACT.email}
                  className="group flex items-center justify-between border-b border-[#1e242a] pb-5 hover:border-[#e8ecef] transition-colors duration-500"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <Mail size={20} strokeWidth={1.4} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a9498]">Email</p>
                      <p className="font-serif text-lg md:text-xl mt-1 text-[#e8ecef] truncate">{BRAND.email}</p>
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="ml-4 shrink-0 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            </div>

            <div>
              <Reveal>
                <p className="text-[11px] tracking-[0.38em] uppercase text-[#8a9498] mb-6">Frequently Asked</p>
              </Reveal>
              <Reveal delay={0.05}>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} data-testid={CONTACT.faq(i)} className="border-b border-[#1e242a]">
                      <AccordionTrigger className="text-left py-5 hover:no-underline text-[#e8ecef]">
                        <span className="font-serif text-lg md:text-xl leading-snug tracking-tight pr-6">{f.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-[#8a9498] text-sm leading-relaxed font-light pb-5">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
